import { Conversion, Fiat, Trade } from "@models";
import { Currency } from "@models/interfaces/currency";
import { Tradable } from "@models/interfaces/tradable";
import { stripFunnyInput } from "./utils";
import { Helper } from "./functions";
import { FiatTransaction, TradableTransaction } from "@models/transaction";

export function convertFiatToTradable(fiat: Fiat, amountFiat: number, priceContainer: Currency | Conversion): number {
	if (priceContainer instanceof Tradable) {
		return (amountFiat * fiat.toEuroRate()) / priceContainer.getPrice();
	}
	if (priceContainer instanceof Conversion) {
		return (amountFiat * fiat.toEuroRate()) / (+priceContainer.fiatAmount() * fiat.toEuroRate());
	}
}

export function convertTradableToFiat(priceContainer: Currency | Conversion, amount: number, fiat: Fiat): number {
	if (priceContainer instanceof Tradable) {
		return (amount * priceContainer.getPrice()) / fiat?.toEuroRate();
	}
	if (priceContainer instanceof Conversion) {
		return +((amount * (+priceContainer.fiatAmount() * fiat?.toEuroRate())) / fiat?.toEuroRate()).toFixed(
			fiat.getPrecisionFiat()
		);
	}
}

export function convertFiatToDefaultFiat(fiat: Fiat, amountFiat: number, targetFiat: Fiat): number {
	return (amountFiat * fiat.toEuroRate()) / targetFiat.toEuroRate();
}

/**
 * balance from given FIAT to EUR
 * @param balance
 * @param fiat
 */
export function toEuroByFiatId(balance: number, fiat: Fiat): number {
	return balance * fiat.toEuroRate();
}

/**
 * balance from EUR to given FIAT
 * @param balance
 * @param fiat
 */
export function toCurrencyByFiatId(balance: number, fiat: Fiat): number {
	const rate = fiat.toEuroRate();
	if (!rate || rate === 0) {
		return balance;
	}
	return balance / rate;
}

/**
 * balance will be calculated into EUR first and then in the targetCurrency
 * @param balance - used for calculation
 * @param targetCurrency - balance will be calculated into this
 * @param currentCurrency - currency which the balance is currently in
 */
export function toTargetCurrency(balance: number, targetCurrency: Fiat, currentCurrency: Fiat): number {
	const balanceInEuro = toEuroByFiatId(balance, currentCurrency);
	return toCurrencyByFiatId(balanceInEuro, targetCurrency);
}

export function convertTradeToTransaction(
	trade: Trade,
	transaction: TradableTransaction,
	fiat: Fiat
): TradableTransaction {
	transaction.setTrade(trade);
	transaction.setType(trade.getType());
	transaction.setAmount(trade.getAmount() ? +trade.getAmount() : +trade.getAmountTradable());
	transaction.setAmountEuro("" + toEuroByFiatId(+trade.getAmountFiat(), fiat));
	transaction.setAmountEurInclFee("" + toEuroByFiatId(+trade.getAmountFiat(), fiat)); // Trades never have tx fees
	transaction.setCurrencyId(trade.getCurrencyId());
	transaction.setCurrentFiatAmount(trade.getAmountFiat());
	transaction.setCurrentFiatId(trade.getFiatId());
	transaction.setWalletId(trade.getWalletId());
	transaction.setIsIndex(trade.getIsIndex());
	transaction.setStatus("finished");
	transaction.setLastChangedTime(trade.getTime());
	transaction.setTime(trade.getTime());
	transaction.setId(trade.getId());
	transaction.setInOrOut(trade.getType() === "sell" ? "outgoing" : "incoming");
	return transaction;
}

export function convertTradeToFiatTransaction(trade: Trade, transaction: FiatTransaction, fiat: Fiat): FiatTransaction {
	transaction.setTrade(trade);
	transaction.setType(trade.getType());
	transaction.setAmount(+trade.getAmountFiat());
	transaction.setAmountWithoutFee(+trade.getAmountFiat());
	transaction.setFiatId(trade.getFiatId());
	transaction.setInOrOut(trade.getType() === "sell" ? "incoming" : "outgoing");
	transaction.setIsIndex(trade.getIsIndex());
	transaction.setIsSavings(trade.getIsSavings());
	transaction.setStatus("finished");
	transaction.setLastChangedTime(trade.getTime());
	transaction.setTime(trade.getTime());
	transaction.setId(trade.getId());
	return transaction;
}

export function tradableToArray(
	val: string | number,
	tradable: Tradable,
	fromTx: boolean = false,
	zeroPrecision: boolean = false
): string[] {
	const value = stripFunnyInput(val.toString());

	let cryptoArray: string[];
	let external: boolean = false;

	if (zeroPrecision) {
		return [parseInt(value) + "", "", ""];
	}

	if (value.includes(".")) {
		cryptoArray = value.split(".");
	} else if (tradable.getPrecisionForTx() !== 0) {
		const emptyDecimals: string = "00000000";
		cryptoArray = [value, emptyDecimals, emptyDecimals];
	} else {
		cryptoArray = [value, "0000", "0000"];
		external = true;
	}
	if (fromTx) {
		cryptoArray[2] = cryptoArray[1].substring(tradable.getPrecision(), 8);
	} else if (tradable.getPrecisionForTx() > 0) {
		cryptoArray[2] = cryptoArray[1].substring(tradable.getPrecision(), tradable.getPrecisionForTx());
	}
	cryptoArray[1] = cryptoArray[1].substring(0, tradable.getPrecision());

	if (tradable.getPrecisionForTx() === 0 && cryptoArray[1].length > 4 && fromTx) {
		cryptoArray[2] = cryptoArray[1].substring(4, cryptoArray[1].length);
		cryptoArray[1] = cryptoArray[1].substring(0, 4);
	}
	if (fromTx && tradable.getPrecisionForTx() === 0 && external) {
		return [value, "", ""];
	}

	// Including decimal as decimal separator and one 0
	let preDecimal = Helper.roundFull(+cryptoArray[0], 0, 1, 1);

	preDecimal = preDecimal.substr(0, preDecimal.length - 1);
	return [preDecimal, cryptoArray[1], cryptoArray[2]];
}
